import { LunchRequest } from '../types/manage-lunch.types';

const escapeHtml = (value: unknown) =>
  String(value ?? '').replace(
    /[&<>"']/g,
    (char) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char] ?? char
  );

/** Requests in the export's order: by unit (department), then by name. */
export const sortFoodRequests = (requests: readonly LunchRequest[]) =>
  [...requests].sort(
    (a, b) =>
      (a.DepartmentName ?? '').localeCompare(b.DepartmentName ?? '') ||
      (a.DisplayName ?? '').localeCompare(b.DisplayName ?? '')
  );

/**
 * One day's meal orders as a printable page (HR Module Navigation Map › Meal Orders: "Export CSV and
 * print the list"): a title, then Name, Unit and Menu, in the export's order, with a count per menu.
 */
export const buildFoodRequestsPrintHtml = (
  title: string,
  requests: readonly LunchRequest[],
  labels: { name: string; unit: string; menu: string; total: string }
) => {
  const sorted = sortFoodRequests(requests);
  const perMenu = new Map<string, number>();
  sorted.forEach((request) =>
    perMenu.set(request.MenuName ?? '', (perMenu.get(request.MenuName ?? '') ?? 0) + 1)
  );
  const rows = sorted
    .map(
      (request) =>
        `<tr><td>${escapeHtml(request.DisplayName)}</td><td>${escapeHtml(request.DepartmentName)}</td><td>${escapeHtml(request.MenuName)}</td></tr>`
    )
    .join('');
  const totals = [...perMenu.entries()]
    .map(([menu, count]) => `<li>${escapeHtml(menu)}: <b>${count}</b></li>`)
    .join('');
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>
<style>
body{font:13px/1.45 system-ui,sans-serif;margin:24px;color:#111}
h1{font-size:18px;margin:0 0 12px}
table{width:100%;border-collapse:collapse}
th,td{text-align:left;padding:6px 8px;border-bottom:1px solid #ddd}
th{font-weight:600;color:#555;border-bottom-color:#999}
ul{padding-left:18px}
</style></head><body>
<h1>${escapeHtml(title)}</h1>
<table><thead><tr><th>${escapeHtml(labels.name)}</th><th>${escapeHtml(labels.unit)}</th><th>${escapeHtml(labels.menu)}</th></tr></thead><tbody>${rows}</tbody></table>
<p><b>${escapeHtml(labels.total)}: ${sorted.length}</b></p><ul>${totals}</ul>
</body></html>`;
};

/** Opens the list in a new window and prints it; false when the browser blocked the window. */
export const printFoodRequests = (html: string): boolean => {
  const win = window.open('', '_blank', 'width=900,height=700');
  if (!win) return false;
  win.document.open();
  win.document.write(html);
  win.document.close();
  win.focus();
  win.print();
  return true;
};
